/**
 * THE VERDICT FROM SCORES ALONE — for a player with no shot stats.
 *
 * `gameVerdict.js` concludes from shot data against a typical player of your
 * handicap, and a player who has never logged a fairway got NO conclusion at
 * all: the page opened on a list of blocks and left the adding-up to them.
 * This is the conclusion a scorecard CAN support, and no more.
 *
 * ONE FRAME, as there: your own scores against par, hole by hole. Nothing here
 * compares you with anyone else, because eighteen totals say nothing about how
 * a typical 14-handicapper scores on par 3s — and nothing here says WHY,
 * because a 5 on a par 4 does not know whether the tee shot or the putter
 * cost the shot.
 *
 * It invents nothing: the headline names the same best/worst hole type the
 * "By hole type" block does (the server picks them). Insights → Player
 * renders the same reading off the same endpoint, which is what stops the two
 * screens telling different stories.
 */
import { PAR_LABEL } from './gameVerdict';

/** Below this many holes of a type, its average is one bad afternoon. */
export const TYPE_HOLE_FLOOR = 9;
/** A spread smaller than this between hole types is not a finding. */
export const TYPE_SPREAD = 0.3;

/** +1.2 / level / −0.4 a hole. */
const perHole = (v) => {
  if (Math.abs(v) < 0.05) return 'level with par';
  return `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(1)} a hole`;
};

/**
 * The headline, from the hole types — or null when the evidence will not carry
 * one. Names ONLY the server's best/worst, so it can never disagree with the
 * "By hole type" block beneath it.
 */
export const scoringHeadline = (byParType) => {
  const types = byParType?.types || {};
  const best = types[byParType?.best];
  const worst = types[byParType?.worst];
  if (!best || !worst) return null;
  if (best.holes < TYPE_HOLE_FLOOR || worst.holes < TYPE_HOLE_FLOOR) return null;

  const spread = worst.avg_to_par - best.avg_to_par;
  if (spread < TYPE_SPREAD) {
    const all = Object.values(types).filter(t => t.holes >= TYPE_HOLE_FLOOR);
    const mean = all.reduce((a, t) => a + t.avg_to_par * t.holes, 0)
      / all.reduce((a, t) => a + t.holes, 0);
    return `You score much the same on every kind of hole — ${perHole(mean)}.`;
  }
  const worstName = PAR_LABEL[byParType.worst];
  const bestName = PAR_LABEL[byParType.best].toLowerCase();
  return `${worstName} cost you the most: ${perHole(worst.avg_to_par)}, `
    + `against ${perHole(best.avg_to_par)} on ${bestName}.`;
};

/**
 * The whole reading, in the shape `GameVerdict` renders.
 *
 * A headline and what it stands on — no supporting lines. The obvious ones
 * (the nines, the trend) are already sentences in the blocks directly beneath,
 * and the review that asked for this verdict also caught the shot verdict
 * printing its recent-scores line twice. The verdict concludes; the blocks
 * are the evidence.
 */
export const buildScoringVerdict = (scoring) => ({
  headline: scoringHeadline(scoring?.by_par_type),
  lines: [],
  focus: null,
  caveat: 'Read from your scorecards alone — they show where your shots go, not why.',
});
